import { call, put, takeLatest, all } from "redux-saga/effects";
import { fetchCurrentUser, fetchUserTemplates, fetchTimezoneList } from "@container/AdminPanel/features/users/usersAPI";
import * as UserService from "@container/AdminPanel/features/users/UserService";
import {
  fetchSessionContextOidcRequest,
  fetchSessionContextOidcSuccess,
  fetchSessionContextOidcError,
  fetchOrganizationViewRequest,
  refreshCurrentUserRequest,
  updateUserView,
} from "../../../common/reducers/sessionContextOidc";
import { wsConnect } from "../../../common/actions/websocketActions";
import { getAuthToken, getOrgId } from "../../../utils/authManager";

function* fetchSessionContextOidcSaga() {
  try {
    // Fetch current user, templates and timezone list in parallel
    const [userResponse, templatesResponse, tzResponse] = yield all([
      call(fetchCurrentUser),
      call(fetchUserTemplates),
      call(fetchTimezoneList)
    ]);

    const tzData = Array.isArray(tzResponse)
      ? tzResponse
      : (tzResponse?.Data || tzResponse?.tzData || []);

    if (userResponse && UserService.isSuccessResponse(userResponse)) {
      // Normalize when the helper exists; otherwise fall back to the raw payload.
      let userView = userResponse;
      if (typeof UserService.normalizeCurrentUser === "function") {
        try {
          userView = UserService.normalizeCurrentUser(userResponse);
        } catch (e) {
          console.error('[sessionContextOidcSaga] normalizeCurrentUser error:', e);
          userView = userResponse;
        }
      }
      
      // Extract templates from the templates response, or fall back to empty array
      let userTemplates = [];
      if (templatesResponse && UserService.isSuccessResponse(templatesResponse)) {
        // Normalize templates if the helper exists
        if (typeof UserService.normalizeTemplatesResponse === "function") {
          try {
            userTemplates = UserService.normalizeTemplatesResponse(templatesResponse);
          } catch (e) {
            console.error('[sessionContextOidcSaga] normalizeTemplatesResponse error:', e);
          }
        } else {
          // Fallback: The response might be the array directly, or wrapped in a templates property
          userTemplates = Array.isArray(templatesResponse) 
            ? templatesResponse 
            : (templatesResponse.templates || []);
        }
      }
      
      const rawUser = (userResponse && (userResponse.user || userResponse)) || {};
      const accessToOrgIds = Array.isArray(rawUser.accessible_orgs) ? rawUser.accessible_orgs : [];

      yield put(fetchSessionContextOidcSuccess({
        userView: userView,
        userTemplates: userTemplates,
        tzData: tzData,
        accessToOrgIds,
        isAuditUser: rawUser.is_audit_user === true,
      }));
      yield put(fetchOrganizationViewRequest());

      // Live updates start here rather than at app boot: bifrost's ticket
      // handshake is authenticated, so it cannot succeed until the user and
      // org context exist.
      const socketUserId = userView?.id ?? rawUser.id ?? rawUser.user_id;
      const socketOrgId = userView?.orgId ?? rawUser.org_id ?? getOrgId();
      if (socketUserId && socketOrgId) {
        yield put(wsConnect({ userId: socketUserId, orgId: socketOrgId }));
      }
    } else {
      const errorMsg = UserService.getErrorMessage
        ? UserService.getErrorMessage(userResponse, "Failed to fetch current user")
        : "Failed to fetch current user";
      yield put(fetchSessionContextOidcError(errorMsg));
    }
  } catch (error) {
    console.error("fetchSessionContextOidcSaga error:", error);
    yield put(
      fetchSessionContextOidcError(
        (error && error.message) || "Failed to fetch current user"
      )
    );
  }
}

// Re-reads the current user without touching loading state or the socket, for
// live events whose payload cannot be applied as-is (e.g. a profile image sent
// as a bare file name rather than a resolvable URL).
function* refreshCurrentUserSaga() {
  try {
    const userResponse = yield call(fetchCurrentUser);
    if (!userResponse || !UserService.isSuccessResponse(userResponse)) return;
    const userView = UserService.normalizeCurrentUser(userResponse);
    if (userView) yield put(updateUserView(userView));
  } catch (error) {
    console.error("refreshCurrentUserSaga error:", error);
  }
}

export default [
  takeLatest(fetchSessionContextOidcRequest.type, fetchSessionContextOidcSaga),
  takeLatest(refreshCurrentUserRequest.type, refreshCurrentUserSaga),
];
